// src/components/opportunity/OpportunityOwnerTransferPanel.tsx
//
// Reassign an opportunity to another org member (mirrors lead transfer).

import { useCallback, useEffect, useState } from "react";
import {
  fetchLeadTransferCandidates,
  type LeadTransferCandidate,
} from "@/lib/lead/transferLeadOwner";
import { reassignOpportunity } from "@/lib/opportunity/opportunityClient";
import type { Opportunity } from "@/types/opportunity";

function displayForMember(m: LeadTransferCandidate): string {
  const n = (m.full_name || m.display_name || "").trim();
  if (n) return `${n}${m.email ? ` · ${m.email}` : ""}`;
  return m.email || m.user_id;
}

export function OpportunityOwnerTransferPanel(props: {
  organizationId: string;
  currentUserId: string;
  opportunity: Opportunity;
  disabled?: boolean;
  onTransferred: () => void;
  setMessage: (msg: { text: string; ok: boolean } | null) => void;
}) {
  const { organizationId, currentUserId, opportunity, disabled, onTransferred, setMessage } =
    props;

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [members, setMembers] = useState<LeadTransferCandidate[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState("");

  const load = useCallback(async () => {
    setLoadErr(null);
    const { data, error } = await fetchLeadTransferCandidates(organizationId);
    if (error) {
      setLoadErr(error);
      setMembers([]);
      return;
    }
    const others = (data ?? []).filter((m) => m.user_id && m.user_id !== currentUserId);
    setMembers(others);
  }, [organizationId, currentUserId]);

  useEffect(() => {
    if (!open) return;
    void load();
  }, [open, load]);

  const onSubmit = async () => {
    const target = selectedUserId.trim();
    if (!target) {
      setMessage({ text: "Choose a teammate to assign this opportunity.", ok: false });
      return;
    }
    setBusy(true);
    try {
      const res = await reassignOpportunity({
        opportunity_id: opportunity.id,
        assigned_user_id: target,
      });
      if (!res.ok) {
        setMessage({
          text: res.error.includes("permission")
            ? "You do not have permission to reassign this opportunity."
            : res.error,
          ok: false,
        });
        return;
      }
      setMessage({ text: "Opportunity reassigned.", ok: true });
      setOpen(false);
      onTransferred();
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        disabled={!!disabled}
        className="text-indigo-600 dark:text-indigo-400 underline underline-offset-2 text-sm disabled:opacity-50"
        onClick={() => setOpen(true)}
      >
        Transfer to another teammate…
      </button>
    );
  }

  return (
    <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-600 bg-slate-50/60 dark:bg-slate-800/30 px-4 py-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium text-slate-700 dark:text-slate-200">
            Transfer this opportunity
          </div>
          <div className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
            Reassign to someone else in your organization.
          </div>
        </div>
        <button
          type="button"
          className="shrink-0 text-xs text-slate-600 dark:text-slate-400 underline"
          onClick={() => setOpen(false)}
        >
          Close
        </button>
      </div>

      <div className="mt-3 space-y-2 border-t border-slate-200 dark:border-slate-600 pt-3">
        {loadErr ? <p className="text-xs text-red-700 dark:text-red-300">{loadErr}</p> : null}
        {members && members.length === 0 && !loadErr ? (
          <p className="text-xs text-slate-600 dark:text-slate-400">
            No other active members in this org.
          </p>
        ) : null}
        {members && members.length > 0 ? (
          <>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
              Teammate
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 dark:bg-zinc-800 dark:border-zinc-600"
            >
              <option value="">Select…</option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {displayForMember(m)}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onSubmit()}
              className="mt-2 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy ? "Transferring…" : "Transfer opportunity"}
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
